import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Steps, Button, message, Space } from 'antd';
import {
  UploadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import UploadCSVStep from './UploadCSVStep';
import PreviewDataStep from './PreviewDataStep';
import ProcessInvoicesStep from './ProcessInvoicesStep';
import VMSInvoiceService from '../../../services/VMSInvoiceService';
import {
  classifyError,
  vmsErrorText,
  getApplicationErrors,
  applicationErrorText,
  isBatchTooLarge
} from './VMSInvoiceErrors';
import Headers from '../../../redux/Actions/Headers/Header';
import ContentCard from 'Components/Card/Card';
import './InvoiceUploadWizard.scss';

const { Step } = Steps;

const InvoiceUploadWizard = () => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [csvFile, setCsvFile] = useState(null);
  const [parsedInvoices, setParsedInvoices] = useState([]);
  const [batchId, setBatchId] = useState(null);
  const [batchGuid, setBatchGuid] = useState(null);
  const [apiTotalInvoices, setApiTotalInvoices] = useState(0);
  const [loading, setLoading] = useState(false);
  const [systemError, setSystemError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [adjustedRows, setAdjustedRows] = useState(new Set());
  const [adjustedCells, setAdjustedCells] = useState(new Set());

  const hasBlockingErrors = validationErrors.some(e => !adjustedRows.has(e.row));

  useEffect(() => {
    dispatch(Headers.setHeader({ title: 'VMS Invoice Upload Wizard' }));
    return () => dispatch(Headers.resetHeader());
  }, [dispatch]);

  const steps = [
    {
      title: 'Upload CSV',
      icon: <UploadOutlined />,
      description: 'Select and upload CSV file'
    },
    {
      title: 'Preview Data',
      icon: <EyeOutlined />,
      description: 'Review invoice data'
    },
    {
      title: 'Process',
      icon: <CheckCircleOutlined />,
      description: 'Upload and process invoices'
    }
  ];

  // Step 1: Handle CSV upload - just store the file
  const handleCSVUpload = (file) => {
    // Set file immediately to enable Next button
    setCsvFile(file);
  };

  // Step 2: Parse and validate when moving to preview
  const handleNextToPreview = async () => {
    if (!csvFile) {
      message.warning('Please select a CSV file to upload.', 4);
      return;
    }

    try {
      setLoading(true);

      const invoices = await VMSInvoiceService.parseCSV(csvFile);
      const totalInvoices = new Set(invoices.map(i => i.InvoiceNumber).filter(Boolean)).size;

      // Validate all invoices
      const errors = [];
      invoices.forEach((invoice, index) => {
        const validation = VMSInvoiceService.validateInvoice(invoice);
        if (!validation.isValid) {
          errors.push({
            row: index + 2, // +2 because of header and 0-index
              invoice: invoice.InvoiceNumber || 'Unknown',
            errors: validation.errors
          });
        }
      });

      setParsedInvoices(invoices);
      setValidationErrors(errors);
      setAdjustedRows(new Set());
      setAdjustedCells(new Set());

      if (errors.length > 0) {
        message.warning(`Found ${errors.length} validation errors. Please review before proceeding.`, 4);
      } else {
        message.success(`Successfully parsed ${totalInvoices} invoices`, 4);
      }

      setLoading(false);
      setCurrentStep(1);
    } catch (error) {
      if (classifyError(error) === 'auth') {
        setLoading(false);
        return;
      }

      const errorMsg = error.response
        ? 'Unable to read the CSV file. Please check the file format and try again.'
        : error.message || 'Unable to read the CSV file. Please check the file format and try again.';
      console.error('Parse CSV error:', error.message);
      message.error(errorMsg, 8);
      setLoading(false);
    }
  };

  // Step 3: Submit to backend with 'pre' status
  const handleSubmitBatch = async () => {
    try {
      setLoading(true);
      setSystemError(null);

      const payload = {
        sageInvoices: parsedInvoices
      };

      const response = await VMSInvoiceService.uploadFile(payload);

      console.log('[DEBUG] uploadFile response status:', response.status);
      console.log('[DEBUG] uploadFile response data:', JSON.stringify(response.data, null, 2));

      if (response.data.success) {
        setBatchId(response.data.batchId ?? response.data.BatchId ?? response.data.id);
        setBatchGuid(response.data.batchGuid ?? response.data.BatchGuid ?? response.data.guid);
        setApiTotalInvoices(response.data.totalInvoices ?? response.data.TotalInvoices ?? parsedInvoices.length);
        message.success(response.data.message, 4);
        setCurrentStep(2);
      } else {
        console.log('[DEBUG] uploadFile non-success response:', JSON.stringify(response.data, null, 2));
        message.error('Unable to create batch. Please try again.', 8);
      }

      setLoading(false);
    } catch (error) {
      console.error('[DEBUG] uploadFile error:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        responseData: error.response?.data,
      });
      const errorMsg = error.response?.data?.message || error.message;
      const reason = classifyError(error);
      if (reason === 'auth') {
        setLoading(false);
        return;
      }

      const applicationErrors = getApplicationErrors(error);
      if (applicationErrors) {
        const data = error.response.data;
        const total = data.totalInvoices ?? parsedInvoices.length;

        setBatchId(data.batchId ?? null);
        setBatchGuid(data.batchGuid ?? null);
        setApiTotalInvoices(total);
        setSystemError({
          tooLarge: isBatchTooLarge(applicationErrors),
          messages: applicationErrors.map(applicationErrorText),
          details: applicationErrors.flatMap((e) => e.details ?? []),
          totalInvoices: total,
          successfulUploads: data.successfulUploads ?? 0,
          failedUploads: data.failedUploads ?? 0,
          notProcessedUploads: data.notProcessedUploads ?? 0
        });
        setCurrentStep(2);
        setLoading(false);
        return;
      }

      if (reason === 400) {
        const apiErrors = error.response.data?.errors;
        let mapped = [];

        if (apiErrors?.length > 0) {
          const parseKeysFromVE = (veString) => {
            const companyMatch = veString?.match(/CompanyKey:\s*(\d+)/);
            const vendorMatch = veString?.match(/VendorLocationKey:\s*(\d+)/);
            return {
              companyKey: companyMatch ? parseInt(companyMatch[1]) : null,
              vendorLocationKey: vendorMatch ? parseInt(vendorMatch[1]) : null,
            };
          };

          mapped = apiErrors.flatMap((apiError) => {
            const { companyKey, vendorLocationKey } = parseKeysFromVE(apiError.validationErrors?.[0]);
            const messages = [];
            if (apiError.errorMessage) messages.push(apiError.errorMessage);
            if (apiError.validationErrors?.length > 0) {
              apiError.validationErrors.forEach(ve =>
                messages.push(typeof ve === 'string' ? ve : ve.message || JSON.stringify(ve))
              );
            }
            if (companyKey === null || messages.length === 0) return [];
            return parsedInvoices.reduce((acc, inv, index) => {
              if (inv.CompanyKey === companyKey && inv.VendorLocationKey === vendorLocationKey) {
                acc.push({
                  row: index + 2,
                  invoice: inv.InvoiceNumber || apiError.invoiceNumber || 'Unknown',
                  errors: messages,
                });
              }
              return acc;
            }, []);
          });
          setValidationErrors(mapped);
          setAdjustedRows(new Set());
          setAdjustedCells(new Set());
        }
        
        if (mapped.length === 0) {
          message.error(
            error.response.data?.message ||
              'The upload was rejected, but the invoices responsible could not be identified. Please contact support.',
            8
          );
        }
      } else {
        message.error(vmsErrorText(reason, {
          timeout: 'Upload timed out. Please try again with a smaller file or check your connection.',
          413: 'File too large. Please reduce the number of invoices and try again.',
          500: 'Server error during upload. Please try again later.',
          fallback: 'Unable to upload invoices. Please try again.'
        }), 8);
      }

      console.error('Upload error:', errorMsg);
      setLoading(false);
    }
  };

  const handleInvoiceUpdate = (rowIndex, field, value) => {
    const invoice = { ...parsedInvoices[rowIndex] };

    // Parse value based on field type
    let parsedValue;
    if (['CompanyKey', 'VendorLocationKey', 'UnitKey', 'CompanyInventoryLinkKey', 'ResidentKey'].includes(field)) {
      parsedValue = parseInt(value) || 0;
    } else if (['Amount', 'Cost', 'Quantity'].includes(field)) {
      parsedValue = parseFloat(value) || 0;
    } else {
      parsedValue = value || '';
    }

    if (invoice[field] === parsedValue) {
      return;
    }

    invoice[field] = parsedValue;
    const updatedInvoices = [...parsedInvoices];
    updatedInvoices[rowIndex] = invoice;
    setParsedInvoices(updatedInvoices);

    const invoiceRows = invoice.InvoiceNumber
      ? updatedInvoices.reduce((rows, inv, idx) => {
          if (inv.InvoiceNumber === invoice.InvoiceNumber) rows.push(idx + 2);
          return rows;
        }, [])
      : [rowIndex + 2];
    setAdjustedRows(prev => {
      const next = new Set(prev);
      invoiceRows.forEach(row => next.add(row));
      return next;
    });
    setAdjustedCells(prev => new Set(prev).add(`${rowIndex + 2}|${field}`));
  };

  // Reset wizard
  const handleReset = () => {
    setCurrentStep(0);
    setCsvFile(null);
    setParsedInvoices([]);
    setBatchId(null);
    setBatchGuid(null);
    setApiTotalInvoices(0);
    setSystemError(null);
    setValidationErrors([]);
    setAdjustedRows(new Set());
    setAdjustedCells(new Set());
  };

  // Navigation handlers
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (currentStep === 0) {
      // Validate Step 1: CSV file
      if (!csvFile) {
        message.warning('Please select a CSV file to upload.', 4);
        return;
      }
      handleNextToPreview();
    } else if (currentStep === 1) {
      // Validate Step 2: No validation errors
      const blockingCount = validationErrors.filter(e => !adjustedRows.has(e.row)).length;
      if (blockingCount > 0) {
        message.warning(`There are ${blockingCount} invalid invoice(s). Please fix them before proceeding.`, 4);
        return;
      }
      handleSubmitBatch();
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <UploadCSVStep
            onUpload={handleCSVUpload}
            loading={loading}
            csvFile={csvFile}
          />
        );
      case 1:
        return (
          <PreviewDataStep
            invoices={parsedInvoices}
            validationErrors={validationErrors}
            adjustedRows={adjustedRows}
            adjustedCells={adjustedCells}
            csvFileName={csvFile?.name}
            onInvoiceUpdate={handleInvoiceUpdate}
          />
        );
      case 2:
        return (
          <ProcessInvoicesStep
            batchId={batchId}
            batchGuid={batchGuid}
            totalInvoices={apiTotalInvoices}
            systemError={systemError}
            onReset={handleReset}
          />
        );
      default:
        return null;
    }
  };

  const getActionButtons = () => {
    const buttons = [];

    if (currentStep > 0 && currentStep < 2) {
      buttons.push(
        <Button
          key="previous"
          data-testid="vmsinvoice-wizard-previous-btn"
          icon={<ArrowLeftOutlined />}
          onClick={handlePrevious}
          disabled={loading}
        >
          Previous
        </Button>
      );
    }

    if (currentStep < 2) {
      buttons.push(
        <Button
          key="next"
          data-testid="vmsinvoice-wizard-next-btn"
          type="primary"
          icon={currentStep === 1 ? <CheckCircleOutlined /> : <ArrowRightOutlined />}
          onClick={handleNext}
          loading={loading}
          disabled={currentStep === 1 && hasBlockingErrors}
        >
          {currentStep === 1 ? 'Upload & Create Batch' : 'Next'}
        </Button>
      );
    }

    if (currentStep === 2) {
      buttons.push(
        <Button
          key="new"
          data-testid="vmsinvoice-wizard-upload-new-btn"
          type="primary"
          onClick={handleReset}
        >
          Upload New Batch
        </Button>
      );
    }

    return buttons;
  };

  return (
    <div className="invoice-upload-wizard">
      <ContentCard>
        <Steps current={currentStep} style={{ marginBottom: 30 }}>
          {steps.map((step, index) => (
            <Step
              key={index}
              title={step.title}
              description={step.description}
              icon={step.icon}
            />
          ))}
        </Steps>

        <div className="step-content">
          {renderStepContent()}
        </div>

        <div className="step-actions" style={{ marginTop: 24, textAlign: 'right' }}>
          <Space>
            {getActionButtons()}
          </Space>
        </div>
      </ContentCard>
    </div>
  );
};

export default InvoiceUploadWizard;
